import React, { useMemo } from 'react';
import { formatTemperatureDelta, temperatureSymbol } from '../../util/temperature';
import { useTemperatureUnit } from '../../util/useTemperatureUnit';

const ANOMALY_BG = 'rgba(192, 57, 43, 0.12)';
const ANOMALY_BORDER = '#c0392b';

/**
 * Ranked table of Isolation Forest ANOMALY batteries only (red-highlighted rows),
 * matching the notebook watchlist rows that land in the anomaly zone.
 */
export default function AnomalyWatchlistTable({ watchlist = [], series = '' }) {
  const temperatureUnit = useTemperatureUnit();
  const unitLabel = temperatureSymbol(temperatureUnit);
  const rows = useMemo(() => {
    const list = Array.isArray(watchlist) ? watchlist : [];
    return list
      .filter((row) => row?.status === 'ANOMALY')
      .sort((a, b) => Number(a.rank ?? 0) - Number(b.rank ?? 0));
  }, [watchlist]);

  if (!rows.length) {
    return (
      <p className="text-muted small mb-0">
        No batteries flagged as anomalies for this selection.
      </p>
    );
  }

  return (
    <div className="anomaly-watchlist-table">
      <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
        <div className="fw-semibold">
          Flagged anomaly batteries
          {series ? ` (${series === 'ALL' ? 'all series' : `series ${series}`})` : ''}
          {` — ${rows.length} device${rows.length === 1 ? '' : 's'}`}
        </div>
        <div className="small text-muted">
          Same red anomaly zone as the unusualness chart
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Battery</th>
              <th scope="col" className="text-end">Score</th>
              <th scope="col" className="text-end">Readings</th>
              <th scope="col" className="text-end">Rise overall {unitLabel}</th>
              <th scope="col" className="text-end">Rise charge {unitLabel}</th>
              <th scope="col" className="text-end">Rise discharge {unitLabel}</th>
              <th scope="col" className="text-end">Variability {unitLabel}</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.battery}
                style={{
                  backgroundColor: ANOMALY_BG,
                  boxShadow: `inset 3px 0 0 ${ANOMALY_BORDER}`,
                }}
              >
                <td>{row.rank}</td>
                <td>
                  <strong>{row.battery}</strong>
                </td>
                <td className="text-end">{row.unusualness_score}</td>
                <td className="text-end">
                  {row.n_readings != null ? Math.round(Number(row.n_readings)).toLocaleString() : '—'}
                </td>
                <td className="text-end">{formatTemperatureDelta(row.rise_overall_F, temperatureUnit)}</td>
                <td className="text-end">{formatTemperatureDelta(row.rise_charging_F, temperatureUnit)}</td>
                <td className="text-end">{formatTemperatureDelta(row.rise_discharging_F, temperatureUnit)}</td>
                <td className="text-end">{formatTemperatureDelta(row.variability_F, temperatureUnit)}</td>
                <td>
                  <span className="badge" style={{ backgroundColor: ANOMALY_BORDER }}>
                    ANOMALY
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
